import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Table, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { useSchoolYear } from '../store/SchoolYearCalculator';
import ErrorModalMessage from '../store/ErrorMessage';
import useWebSocketRoom from '../store/useWebSocketRoom';
import { buildStaffAttendanceRoom } from '../store/websocketRooms';
import {
  FilterBar,
  SearchInput,
  FilterSelect,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

// Rows precompute their search text (see eventProgramRows), so the accessor is a
// stable module-level lookup and stays out of the hook's memo churn.
const EVENT_PROGRAM_SEARCH_ACCESSOR = (row) => row.searchText;

const EVENT_PROGRAM_FILTERS = [
  {
    key: 'access',
    label: 'Access',
    allOption: { value: 'all', label: 'All events' },
    options: [
      { value: 'public', label: 'Public only' },
      { value: 'private', label: 'Private only' },
    ],
    // need to use 15 for local dev and 13 for production/live for event_type_id
    predicate: (row, value) =>
      value === 'public'
        ? row.eventProgram.event_type_id === 4
        : row.eventProgram.event_type_id === 13,
  },
  {
    key: 'schedule',
    label: 'Schedule',
    allOption: { value: 'all', label: 'All schedules' },
    options: [
      { value: 'with_dates', label: 'With dates' },
      { value: 'without_dates', label: 'Without dates' },
    ],
    predicate: (row, value) =>
      value === 'with_dates'
        ? row.scheduleSummary.dateCount > 0
        : row.scheduleSummary.dateCount === 0,
  },
];

const EVENT_PROGRAM_SORT_OPTIONS = [
  {
    key: 'name',
    label: 'Name',
    accessor: (row) => row.eventProgram.name?.toLowerCase() ?? '',
  },
  {
    key: 'dateCount',
    label: 'Date count',
    accessor: (row) => row.scheduleSummary.dateCount,
    type: 'number',
  },
  {
    key: 'location',
    label: 'Location',
    accessor: (row, ctx) =>
      ctx.getLocationLabel(row.eventProgram.location_id)?.toLowerCase() ?? '',
  },
  {
    key: 'type',
    label: 'Type',
    accessor: (row, ctx) =>
      ctx.getEventTypeLabel(row.eventProgram.event_type_id)?.toLowerCase() ?? '',
  },
];

const EventsProgramComponent = () => {
  const [eventsPrograms, setEventsPrograms] = useState([]);
  const [currentEventProgram, setCurrentEventProgram] = useState({
    event_template_id: null,
    name: null,
    description: '',
    location_id: null,
    coordinator_id: '',
    event_type_id: 4,
  });
  const [currentEventDate, setCurrentEventDate ] = useState({
    event_dates_id: null,
    event_id: null, 
    date: null,
    start_time: null, 
    end_time: null,
    total_seats: null,
    manual_sold_out: false,
    sold_out: false,
    submission_count: 0,
    available_seats: null,
  })
  const [ eventDates, setEventDates ] = useState([]);
  const [schools, setSchools] = useState([]);
  const [dateSchoolLinks, setDateSchoolLinks] = useState([]);
  const [dateSchools, setDateSchools] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [ showModalDateEvent, setShowModalDateEvent] = useState(false);
  const { authState } = useAuth();
  const [openStates, setOpenStates] = useState({});
  const navigate = useNavigate();
  const [ eventTypes, setEventTypes] = useState([]);
  const [ locations, setLocations] = useState([]);
  const [ userNames, setUsersNames] = useState([]);
  const [ eventTemplates, setEventTemplates ] = useState([]);
  const [newDateModal, setNewDateModal] = useState(false);
  const [showEventDateModal, setShowEventDateModal] = useState(false);
  const [showDeletionError, setShowDeletionError] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const {currentYear, schoolYearListParent } = useSchoolYear();
  const [schoolYearList, setSchoolYearList] = useState([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState();
  const [errorMessage, setErrorMessage] = useState('');
  const [dateEventProgram, setDateEventProgram] = useState(null);

  const getSelectedSchoolYearId = () => (
    selectedSchoolYear ? parseInt(selectedSchoolYear, 10) : undefined
  );

  const createEmptyEventProgram = () => ({
    event_template_id: null,
    name: '',
    description: '',
    location_id: null,
    coordinator_id: '',
    event_type_id: 4,
    school_year_id: getSelectedSchoolYearId(),
  });

  const createEmptyEventDate = (eventId = null) => ({
    event_dates_id: null,
    event_id: eventId,
    date: null,
    start_time: null,
    end_time: null,
    total_seats: null,
    manual_sold_out: false,
    sold_out: false,
    submission_count: 0,
    available_seats: null,
    school_year_id: getSelectedSchoolYearId(),
  });

  const createEmptyDateSchool = () => ({
    event_date_id: null,
    school_id: '',
    school_year_id: getSelectedSchoolYearId(),
    number_of_students: '',
    number_of_teachers: '',
  });

  // need to use 15 for local dev and 13 for production/live when it comes using ep.event_type_id and 
  const isDateProgramPrivate = dateEventProgram?.event_type_id === 13;


  useEffect(() => {
    if (!authState) {
      navigate('/login');
    }
  }, [authState, navigate]);

  useEffect(() => {
    if (currentYear && schoolYearListParent.length) {
      setSelectedSchoolYear(currentYear);
      setSchoolYearList(schoolYearListParent);
    }
  }, [currentYear, schoolYearListParent]);

  useEffect(() => {
    if(selectedSchoolYear){
      fetchEventsPrograms();
      fetchEventDates();
      fetchDateSchoolLinks();
    }
  }, [selectedSchoolYear]);

  useEffect(() => {
    fetchEventTypes();
    fetchLocations();
    fetchUserNames();
    fetchEventTemplates();
    fetchSchools();
  }, []);

  useWebSocketRoom({
    roomName: buildStaffAttendanceRoom(getSelectedSchoolYearId()),
    enabled: Boolean(getSelectedSchoolYearId()),
    onMessage: () => {
      fetchEventsPrograms();
      fetchEventDates();
      fetchDateSchoolLinks();
    },
  });





  const fetchEventsPrograms = async () => {
    try {const response = await fetch(`${backendURL}/api/events-programs/year/${selectedSchoolYear}`, {
      method: 'GET',
    //   credentials: 'include',
    });
    const data = await response.json();
    const filteredEvents = data.filter(
      // need to use 15 for local dev and 13 for production/live when it comes using ep.event_type_id and 
      event => event.event_type_id === 4 || event.event_type_id === 13
    );
    setEventsPrograms(filteredEvents);}
    catch {
      ErrorModalMessage('error fetching program events')
    }
  };

  const fetchEventTemplates = async () => {
    const response = await fetch(`${backendURL}/api/event-templates`, {
      method: "GET",
      credentials: "include"
    })
    const data = await response.json();
    setEventTemplates(data)
  }

  const fetchEventTypes = async () => {
    const response = await fetch(`${backendURL}/api/event-types`, {
      method: 'GET',
      credentials: 'include'
    });
    try{
      const data = await response.json();
      setEventTypes(data);
    } catch (error) {
      console.error("Error fetching the event types: ", error.message)
    }
  }
  const fetchUserNames = async () => {
    try {
        const response = await fetch (`${backendURL}/api/app-users`,{
            method: 'GET',
            credentials: 'include'
          });
        const data = await response.json();
        if(!response.ok) throw new Error ('Failed to fetch user data')
        // console.log("Users Data: ",data)
        setUsersNames(data);
    } catch (err) {
        console.error(err.message);
    }
}
  const fetchLocations = async () => {
    const response = await fetch(`${backendURL}/api/locations`, {
      method: 'GET',
      credentials: 'include'
    });
    try{
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      console.error("Error fetching locations: ", error.message)
    }
  }

  const fetchSchools = async () => {
    try {
      const response = await fetch(`${backendURL}/api/schools`, {
        method: 'GET',
        credentials: 'include'
      });
      const data = await response.json();
      setSchools(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching schools: ", error.message);
    }
  }

  const fetchEventDates = async () => {
    try {const response = await fetch(`${backendURL}/api/event-dates/year/${selectedSchoolYear}`, {
        method: 'GET',
        credentials: 'include'
    });
    const data = await response.json();
    const sortedData = data.sort((a, b) => new Date(a.date) - new Date(b.date));
      setEventDates(sortedData);
    }
    catch (error) {
        ErrorModalMessage("Error fetching event dates: ", error.message);
    }
  }

  const fetchDateSchools = async (eventDateId) => {
    try {
      const response = await fetch(`${backendURL}/api/dates-schools/${eventDateId}`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        setDateSchools([createEmptyDateSchool()]);
        return;
      }

      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        setDateSchools([createEmptyDateSchool()]);
        return;
      }

      setDateSchools(data.map((link) => ({
        event_date_id: link.event_date_id,
        school_id: link.school_id ?? '',
        school_year_id: link.school_year_id ?? getSelectedSchoolYearId(),
        number_of_students: link.number_of_students ?? '',
        number_of_teachers: link.number_of_teachers ?? '',
      })));
    } catch (error) {
      console.error("Error fetching date schools: ", error.message);
      setDateSchools([createEmptyDateSchool()]);
    }
  };

  const fetchDateSchoolLinks = async () => {
    try {
      const response = await fetch(`${backendURL}/api/dates-schools/year/${selectedSchoolYear}`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        setDateSchoolLinks([]);
        return;
      }

      const data = await response.json();
      setDateSchoolLinks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching date school links: ", error.message);
      setDateSchoolLinks([]);
    }
  };
  const handleYearChange = (event) => {
      setSelectedSchoolYear(event.target.value);
    };

  const normalizeOptionalInt = (value) => (
    value === '' || value === null || value === undefined ? null : parseInt(value, 10)
  );

  const getProgramForDate = (eventId) => (
    eventsPrograms.find((eventProgram) => eventProgram.event_id === eventId) || null
  );

  const handleEventFormChange = (e) => {
    setCurrentEventProgram({ 
      ...currentEventProgram,
       [e.target.name]: e.target.value,
        school_year_id: getSelectedSchoolYearId(),
       });
  };

  const handleAvailabilityChange = (event) => {
    setCurrentEventProgram((prevState) => ({
      ...prevState,
      // need to use 15 for local dev and 13 for production/live when it comes using ep.event_type_id and 
      event_type_id: event.target.checked ? 4 : 13,
      school_year_id: getSelectedSchoolYearId(),
    }));
  };

  const handleEventDateFormChange = (event) => {
    const { name, type, checked, value } = event.target;

    if (type === "checkbox") {
        setCurrentEventDate(prevState => ({
            ...prevState,
            [name]: checked,
            school_year_id: getSelectedSchoolYearId(),
        }));
    } else if (type === "number") {
        setCurrentEventDate(prevState => ({
            ...prevState,
            [name]: value === '' ? '' : parseInt(value, 10),
            school_year_id: getSelectedSchoolYearId(),
        }));
    } else {
        setCurrentEventDate(prevState => ({
            ...prevState,
            [name]: value,
            school_year_id: getSelectedSchoolYearId(),
        }));
    }
};

  const handleDateSchoolFormChange = (index, field, value) => {
    setDateSchools((prevState) => prevState.map((dateSchool, currentIndex) => {
      if (currentIndex !== index) {
        return dateSchool;
      }

      return {
        ...dateSchool,
        [field]: value,
        school_year_id: getSelectedSchoolYearId(),
      };
    }));
  };

  const addDateSchool = () => {
    setDateSchools((prevState) => [...prevState, createEmptyDateSchool()]);
  };

  const removeDateSchool = (index) => {
    setDateSchools((prevState) => {
      const nextState = prevState.filter((_, currentIndex) => currentIndex !== index);
      return nextState.length > 0 ? nextState : [createEmptyDateSchool()];
    });
  };



  const handleEventSubmit = async (e) => {
    e.preventDefault();
    const method = currentEventProgram.event_id ? 'PUT' : 'POST';
    const url = currentEventProgram.event_id ? `${backendURL}/api/events-programs/${currentEventProgram.event_id}` : `${backendURL}/api/events-programs`;
    const payload = {
      ...currentEventProgram,
      event_template_id: normalizeOptionalInt(currentEventProgram.event_template_id),
      location_id: normalizeOptionalInt(currentEventProgram.location_id),
      coordinator_id: normalizeOptionalInt(currentEventProgram.coordinator_id),
      event_type_id: currentEventProgram.event_type_id,
      school_year_id: getSelectedSchoolYearId(),
    };
    const response = await fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });

    if (!response.ok) {
      setErrorMessage('Unable to save the event program.');
      return;
    }

    setCurrentEventProgram(createEmptyEventProgram());
    setShowForm(false);
    fetchEventsPrograms();
  };

  const handleNewEventDate = async (eventProgram) => {
    setDateEventProgram(eventProgram);
    setCurrentEventDate(createEmptyEventDate(eventProgram.event_id));
    // need to use 15 for local dev and 13 for production/live when it comes using ep.event_type_id and
    setDateSchools(eventProgram.event_type_id === 13 ? [createEmptyDateSchool()] : []);
    setShowEventDateModal(true); // Use the same modal for creating a new event date
  };

  const handleEventDateSubmit = async (e) => {
    e.preventDefault();

    if (!currentEventDate.date || !currentEventDate.start_time || !currentEventDate.end_time) {
      setErrorMessage('Please be sure to fill out all required fields (Date, Start Time, End Time).');
      return; // Prevent form submission if required fields are missing
  }

    const normalizedDateSchools = dateSchools
      .filter((dateSchool) => (
        dateSchool.school_id !== '' ||
        dateSchool.number_of_students !== '' ||
        dateSchool.number_of_teachers !== ''
      ))
      .map((dateSchool) => ({
        event_date_id: currentEventDate.event_dates_id,
        school_id: normalizeOptionalInt(dateSchool.school_id),
        school_year_id: getSelectedSchoolYearId(),
        number_of_students: normalizeOptionalInt(dateSchool.number_of_students),
        number_of_teachers: normalizeOptionalInt(dateSchool.number_of_teachers),
      }));

    if (isDateProgramPrivate) {
      if (normalizedDateSchools.length === 0) {
        setErrorMessage('Please add at least one school with student and teacher counts.');
        return;
      }

      const hasIncompleteSchoolRows = normalizedDateSchools.some((dateSchool) => (
        !dateSchool.school_id ||
        dateSchool.number_of_students === null ||
        dateSchool.number_of_teachers === null
      ));

      if (hasIncompleteSchoolRows) {
        setErrorMessage('Please complete the school, number of students, and number of teachers for each private attendance row.');
        return;
      }

      const uniqueSchoolIds = new Set(normalizedDateSchools.map((dateSchool) => dateSchool.school_id));
      if (uniqueSchoolIds.size !== normalizedDateSchools.length) {
        setErrorMessage('Please select each school only once per event date.');
        return;
      }
    }

    const method = currentEventDate.event_dates_id ? 'PUT' : 'POST';
    const url = currentEventDate.event_dates_id ? `${backendURL}/api/event-dates/${currentEventDate.event_dates_id}` : `${backendURL}/api/event-dates`;
    const eventDatePayload = {
      ...currentEventDate,
      event_id: currentEventDate.event_id,
      total_seats: normalizeOptionalInt(currentEventDate.total_seats),
      sold_out: Boolean(currentEventDate.manual_sold_out),
      school_year_id: getSelectedSchoolYearId(),
    };
    const response = await fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventDatePayload),
      credentials: 'include',
    });

    if (!response.ok) {
      setErrorMessage('Unable to save the event date.');
      return;
    }

    const savedEventDate = await response.json();

    await fetch(`${backendURL}/api/dates-schools/${savedEventDate.event_dates_id}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (isDateProgramPrivate) {
      const dateSchoolRequests = normalizedDateSchools.map((dateSchool) => fetch(
        `${backendURL}/api/dates-schools/${savedEventDate.event_dates_id}/${dateSchool.school_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...dateSchool,
            event_date_id: savedEventDate.event_dates_id,
          }),
          credentials: 'include',
        }
      ));

      const dateSchoolResponses = await Promise.all(dateSchoolRequests);
      if (dateSchoolResponses.some((dateSchoolResponse) => !dateSchoolResponse.ok)) {
        setErrorMessage('Unable to save the school attendance counts for this event date.');
        return;
      }
    }

    setCurrentEventDate(createEmptyEventDate());
    setDateEventProgram(null);
    setDateSchools([]);
    setShowEventDateModal(false);
    setShowModalDateEvent(false);
    setNewDateModal(false);
    fetchEventDates();
    fetchDateSchoolLinks();
  };

  const editEventProgram = (eventProgram) => {
    setCurrentEventProgram({
      ...eventProgram,
      school_year_id: getSelectedSchoolYearId(),
    });
    setShowForm(true);
  };

  const editEventDate = async (eventDate) => {
    const eventProgram = getProgramForDate(eventDate.event_id);
    setDateEventProgram(eventProgram);
    setCurrentEventDate({
        ...eventDate,
        school_year_id: getSelectedSchoolYearId(),
    });

    // need to use 15 for local dev and 13 for production/live when it comes using ep.event_type_id and
    if (eventProgram?.event_type_id === 13) {
      await fetchDateSchools(eventDate.event_dates_id);
    } else {
      setDateSchools([]);
    }

    setShowEventDateModal(true); // Use the same modal for editing an existing event date
};


  const handleCancel = () => {
    setCurrentEventDate(createEmptyEventDate());
    setCurrentEventProgram(createEmptyEventProgram());
    setDateEventProgram(null);
    setDateSchools([]);
    setShowForm(false);
    setShowEventDateModal(false);
    setShowModalDateEvent(false);
    setNewDateModal(false);
  }

  const handleShowForm = () => {
    setCurrentEventProgram(createEmptyEventProgram());
    setShowForm(!showForm);
  }

  const deleteEventProgram = async (event_id) => {
    
     try {
      const response = await fetch(`${backendURL}/api/events-programs/${event_id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (response.ok) {
      fetchEventsPrograms();
    } else {
      console.log("Unable to delete the associated event");
    }
  }
    catch (error) {
      setShowDeletionError(true);
      console.error("Please delete associated event dates before deleting an event.");
    }
  };

  const handleDeleteEventDate = () => {
    setConfirmationMessage('Are you sure you want to delete this event date?')
    setShowConfirmationModal(true);
  }
  
  const deleteEventDate = async (eventDateId) => {
    try {
      await fetch(`${backendURL}/api/dates-schools/${eventDateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const response = await fetch(`${backendURL}/api/event-dates/${eventDateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        handleCancel();
        setShowConfirmationModal(false);
        fetchEventDates();
        fetchDateSchoolLinks();
      } else {
        throw new Error('Failed to delete the event date');
      }
    } catch (error) {
      console.error("Deletion failed:", error);
      // Optionally, handle error showing in UI
    }
  }

  const getEventTypeLabel = (eventTypeId) => {
    const eventTypeObj = eventTypes.find(eventType => eventType.event_type_id === eventTypeId);
    return eventTypeObj ? eventTypeObj.name : 'Event type failed to load';
  };

  const getEventType = (eventTypeId) => getEventTypeLabel(eventTypeId);

  const getLocationLabel = (locationId) => {
    const locationObj = locations.find(location => location.location_id === locationId);
    return locationObj ? locationObj.name : 'Location failed to load';
  };

  const getLocation = (locationId) => getLocationLabel(locationId);

  const getCoordinatorId = (userId) => {
    const nameObject = userNames.find(user => user.user_id === userId);
    return nameObject ? (`${nameObject.first_name} ${nameObject.last_name}`) : 'Failed to get User';
  };

  const getEventAccessLabel = (eventTypeId) => (
    eventTypeId === 4 ? 'Public Form' : 'Private Counts'
  );

  const getEventAccessSearchTerms = (eventTypeId) => (
    eventTypeId === 4
      ? 'public form available for form submissions submission'
      : 'private attendance counts hidden unlisted admin'
  );

  const formatTime = (timeString) => {
    // Assuming timeString is in 'HH:MM:SS' format
    if (timeString === null) {
      return `no time set`;
    }

    const [hours, minutes] = timeString.split(':');
    const hoursInt = parseInt(hours, 10);
    const suffix = hoursInt >= 12 ? 'PM' : 'AM';
    const formattedHours = ((hoursInt + 11) % 12 + 1); // Convert 24hr to 12hr format
    return `${formattedHours}:${minutes} ${suffix}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'No date set';
    }

    const parsedDate = new Date(`${dateString}T12:00:00`);
    return parsedDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getSchoolName = (schoolId) => {
    const school = schools.find((schoolRecord) => schoolRecord.school_id === Number(schoolId));
    return school ? school.name : 'Unknown school';
  };

  const getEventDateSubmissionCount = (eventDate) => (
    parseInt(eventDate?.submission_count, 10) || 0
  );

  const getEventDateAvailableSeats = (eventDate) => {
    const totalSeats = normalizeOptionalInt(eventDate?.total_seats);
    if (totalSeats === null) {
      return null;
    }

    return Math.max(totalSeats - getEventDateSubmissionCount(eventDate), 0);
  };

  const isEventDateSoldOut = (eventDate) => {
    const availableSeats = getEventDateAvailableSeats(eventDate);
    return Boolean(eventDate?.manual_sold_out) || (availableSeats !== null && availableSeats === 0);
  };

  const getAvailableSeatsLabel = (eventDate) => {
    const totalSeats = normalizeOptionalInt(eventDate?.total_seats);
    const availableSeats = getEventDateAvailableSeats(eventDate);

    if (totalSeats === null || availableSeats === null) {
      return 'Seat cap not set';
    }

    return `${availableSeats} of ${totalSeats} seat${totalSeats === 1 ? '' : 's'} left`;
  };

  const getProgramEventDates = (eventId) => (
    eventDates
      .filter((eventDate) => eventDate.event_id === eventId)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
  );

  const getDateSchoolLinksForDate = (eventDateId) => (
    dateSchoolLinks.filter((link) => link.event_date_id === Number(eventDateId))
  );

  const getEventScheduleSummary = (eventProgram) => {
    const programDates = getProgramEventDates(eventProgram.event_id);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextUpcomingDate = programDates.find((eventDate) => {
      const parsedDate = new Date(`${eventDate.date}T00:00:00`);
      return parsedDate >= today;
    }) || null;
    const latestDate = programDates[programDates.length - 1] || null;
    const soldOutDates = programDates.filter((eventDate) => isEventDateSoldOut(eventDate)).length;
    const capacityTrackedDates = programDates.filter(
      (eventDate) => normalizeOptionalInt(eventDate.total_seats) !== null
    ).length;
    const totalAvailableSeats = programDates.reduce(
      (total, eventDate) => total + (getEventDateAvailableSeats(eventDate) ?? 0),
      0
    );
    const totalSubmittedRequests = programDates.reduce(
      (total, eventDate) => total + getEventDateSubmissionCount(eventDate),
      0
    );
    const programDateIds = new Set(programDates.map((eventDate) => eventDate.event_dates_id));
    const privateDateLinks = dateSchoolLinks.filter((link) => programDateIds.has(link.event_date_id));
    const privateSchools = new Set(privateDateLinks.map((link) => link.school_id)).size;
    const privateStudents = privateDateLinks.reduce(
      (total, link) => total + (parseInt(link.number_of_students, 10) || 0),
      0
    );
    const privateTeachers = privateDateLinks.reduce(
      (total, link) => total + (parseInt(link.number_of_teachers, 10) || 0),
      0
    );

    return {
      dateCount: programDates.length,
      nextDate: nextUpcomingDate ? formatDate(nextUpcomingDate.date) : 'No upcoming dates',
      latestDate: latestDate ? formatDate(latestDate.date) : 'No dates added',
      soldOutDates,
      capacityTrackedDates,
      totalAvailableSeats,
      totalSubmittedRequests,
      privateSchools,
      privateStudents,
      privateTeachers,
    };
  };

  const getEventProgramSearchText = (eventProgram, scheduleSummary) => {
    const programDates = getProgramEventDates(eventProgram.event_id);
    const linkedSchoolNames = [
      ...new Set(
        programDates.flatMap((eventDate) =>
          getDateSchoolLinksForDate(eventDate.event_dates_id).map((link) => getSchoolName(link.school_id))
        )
      ),
    ];
    const dateTerms = programDates.flatMap((eventDate) => [
      eventDate.date,
      formatDate(eventDate.date),
      isEventDateSoldOut(eventDate) ? 'sold out' : 'open',
      `${getEventDateSubmissionCount(eventDate)} submissions requested`,
      getAvailableSeatsLabel(eventDate),
    ]);

    return [
      eventProgram.name,
      eventProgram.description,
      getEventTypeLabel(eventProgram.event_type_id),
      getLocationLabel(eventProgram.location_id),
      getCoordinatorId(eventProgram.coordinator_id),
      getEventAccessSearchTerms(eventProgram.event_type_id),
      scheduleSummary.nextDate,
      scheduleSummary.latestDate,
      scheduleSummary.dateCount > 0 ? 'has dates scheduled' : 'no dates unscheduled',
      ...dateTerms,
      ...linkedSchoolNames,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  };

  const eventProgramRows = useMemo(
    () => eventsPrograms.map((eventProgram) => {
      const scheduleSummary = getEventScheduleSummary(eventProgram);
      return {
        eventProgram,
        scheduleSummary,
        searchText: getEventProgramSearchText(eventProgram, scheduleSummary),
      };
    }),
    // getEventScheduleSummary / getEventProgramSearchText are stable over these
    // state arrays (dates, links, and the lookup lists behind the label helpers).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventsPrograms, eventDates, dateSchoolLinks, schools, eventTypes, locations, userNames]
  );

  const listContext = useMemo(
    () => ({ getLocationLabel, getEventTypeLabel }),
    // Both helpers only read these lookup lists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locations, eventTypes]
  );

  const {
    items: filteredEventProgramRows,
    resultCount,
    totalCount,
    isFiltered,
    search,
    filterList,
    sort,
    clear,
  } = useListControls(eventProgramRows, {
    searchAccessor: EVENT_PROGRAM_SEARCH_ACCESSOR,
    filters: EVENT_PROGRAM_FILTERS,
    sortOptions: EVENT_PROGRAM_SORT_OPTIONS,
    context: listContext,
  });

  const renderEventDates = (eventProgram) => {
    const programDates = getProgramEventDates(eventProgram.event_id);

    if (programDates.length === 0) {
      return (
        <div className="schedule-empty-state">
          No dates have been created for this event yet.
        </div>
      );
    }

    return (
      <div className="schedule-card-grid">
        {programDates.map((eventDate) => {
          const linkedSchools = getDateSchoolLinksForDate(eventDate.event_dates_id);
          const totalStudents = linkedSchools.reduce(
            (total, link) => total + (parseInt(link.number_of_students, 10) || 0),
            0
          );
          const totalTeachers = linkedSchools.reduce(
            (total, link) => total + (parseInt(link.number_of_teachers, 10) || 0),
            0
          );
          const submissionCount = getEventDateSubmissionCount(eventDate);
          const availableSeats = getEventDateAvailableSeats(eventDate);
          const schoolPreview = linkedSchools
            .slice(0, 3)
            .map((link) => getSchoolName(link.school_id))
            .join(', ');

          return (
            <button
              type="button"
              key={eventDate.event_dates_id}
              className="schedule-card"
              onClick={() => editEventDate(eventDate)}
            >
              <div className="schedule-card-header">
                <span className="schedule-card-title">{formatDate(eventDate.date)}</span>
                {isEventDateSoldOut(eventDate) ? (
                  <span className="schedule-card-status sold-out">Sold Out</span>
                ) : (
                  <span className="schedule-card-status open">Open</span>
                )}
              </div>
              <div className="schedule-card-time">
                {`${formatTime(eventDate.start_time)} to ${formatTime(eventDate.end_time)}`}
              </div>
              <div className="schedule-card-meta">
                {`Seats: ${eventDate.total_seats ?? 'Not set'}`}
              </div>
              {/* need to use 15 for local dev and 13 for production/live when it comes using ep.event_type_id and */}
              {eventProgram.event_type_id === 13 ? (
                <>
                  <div className="schedule-card-meta">
                    {`${linkedSchools.length} school${linkedSchools.length === 1 ? '' : 's'} linked`}
                  </div>
                  <div className="schedule-card-meta">
                    {`${totalStudents} students, ${totalTeachers} teachers`}
                  </div>
                  <div className="schedule-card-preview">
                    {schoolPreview || 'No school counts added yet'}
                    {linkedSchools.length > 3 ? '...' : ''}
                  </div>
                </>
              ) : (
                <>
                  <div className="schedule-card-meta">
                    {`Form submissions: ${submissionCount}`}
                  </div>
                  <div className="schedule-card-meta">
                    {`Available seats: ${availableSeats ?? 'Seat cap not set'}`}
                  </div>
                  <div className="schedule-card-preview">
                    {availableSeats === null
                      ? 'Public event date. Attendance comes from form submissions. Set a seat cap to track availability.'
                      : `${getAvailableSeatsLabel(eventDate)}.`}
                  </div>
                </>
              )}
              <div className="schedule-card-action">Click to edit date</div>
            </button>
          );
        })}
      </div>
    );
  };

  const toggleOpenState = (eventId) => {
    setOpenStates((prevOpenStates) => ({
      ...prevOpenStates,
      [eventId]: !prevOpenStates[eventId],
    }));
  };
  

  return (
    <div>
      <Button onClick={() => handleShowForm()} className="m-1">
        {showForm ? 'Show Events Programs List' : 'Add New Event Program'}
      </Button>
      <div>
          <Dropdown>
            <span className='fw-bold'>Select School Year</span>
          <select value={selectedSchoolYear} onChange={handleYearChange}>
              <option value="" disabled>Select School Year</option>
              {schoolYearList.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.school_year}
                </option>
              ))}
            </select>
          </Dropdown>
      </div>

      {showForm ? (
        <Form onSubmit={handleEventSubmit}>
          <Form.Group controlId="event_template_id">
            <Form.Label>Event Template ID</Form.Label>
            <Form.Control
              as="select"
              name="event_template_id"
              value={currentEventProgram.event_template_id ?? ''}
              onChange={handleEventFormChange}
            >
              {<option value={''}>Select Event Template</option>}
            {eventTemplates.map( eventTemplate => (
            <option value={eventTemplate.template_id} key={eventTemplate.template_id}>{eventTemplate.name}</option>))
            }
            </Form.Control>
          </Form.Group>

          <Form.Group controlId="name">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter event name"
              name="name"
              value={currentEventProgram.name ?? ''}
              onChange={handleEventFormChange}
            />
          </Form.Group>

          <Form.Group controlId="description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter event description"
              name="description"
              value={currentEventProgram.description ?? ''}
              onChange={handleEventFormChange}
            />
          </Form.Group>

          <Form.Group controlId="location_id">
            <Form.Label>Location ID</Form.Label>
            <Form.Control
              as={'select'}
              name="location_id"
              value={currentEventProgram.location_id ?? ''}
              onChange={handleEventFormChange}
            >
              <option value={''}>{"Select Location"}</option>
            {locations.map( location => (
            <option key={location.location_id} value={location.location_id}>{location.name}</option>
            ))}
            </Form.Control>
          </Form.Group>

          <Form.Group controlId="coordinator_id">
            <Form.Label>Coordinator ID</Form.Label>
            <Form.Control
              as={'select'}
              name="coordinator_id"
              value={currentEventProgram.coordinator_id ?? ''}
              onChange={handleEventFormChange}
            >
              <option value={''}>Select Coordinator</option>
              {userNames.map(userName => (
                <option value={userName.user_id} key={userName.user_id}>{userName.first_name} {userName.last_name}</option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group controlId="available_for_form_submissions" className="mb-3">
            <Form.Check
              type="switch"
              label="Available for form submissions"
              checked={currentEventProgram.event_type_id === 4}
              onChange={handleAvailabilityChange}
            />
            <Form.Text muted>
              Public attendance events use individual form submissions. Private attendance events use per-school counts on each event date.
            </Form.Text>
          </Form.Group>

          <Button type="submit" className="m-1">Submit</Button>
          <Button type="button" variant="secondary" className="m-1" onClick={handleCancel}>Cancel</Button>
        </Form>
      ) : (
        <>
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="event"
                onClear={clear}
                isFiltered={isFiltered}
                hint="Search matches event names, descriptions, dates, coordinators, locations, and linked private schools."
              />
            )}
          >
            <SearchInput
              label="Search events"
              placeholder="Names, dates, locations, coordinators, or schools"
              {...search}
            />
            {filterList.map((f) => (
              <FilterSelect key={f.filterKey} {...f} />
            ))}
            <SortControl {...sort} />
          </FilterBar>

        <Table striped bordered hover>
      <thead>
        <tr>
          <th>Attendance Events</th>
        </tr>
      </thead>
      <tbody>
        {filteredEventProgramRows.length === 0 ? (
          <tr>
            <td>
              <div className="schedule-empty-state">
                No attendance events match the current filters.
              </div>
            </td>
          </tr>
        ) : filteredEventProgramRows.map(({ eventProgram, scheduleSummary }) => {
          const isOpen = openStates[eventProgram.event_id] || false;
          const rowDateSummary =
            scheduleSummary.dateCount === 0
              ? 'No dates added'
              : scheduleSummary.nextDate !== 'No upcoming dates'
                ? `Next: ${scheduleSummary.nextDate}`
                : `Most recent: ${scheduleSummary.latestDate}`;

          return (
            <React.Fragment key={eventProgram.event_id}>
              <tr
                className={`compact-row${isOpen ? ' is-open' : ''}`}
                onClick={() => toggleOpenState(eventProgram.event_id)}
                style={{ cursor: 'pointer' }}
              >
                <td colSpan={1}>
                  <div className="event-program-row-content">
                    <div className="event-program-main">
                      <div className="event-program-name">{eventProgram.name}</div>
                      <div className="event-program-type">{getEventType(eventProgram.event_type_id)}</div>
                      <div className="event-program-description">
                        {eventProgram.description || 'No description provided.'}
                      </div>
                    </div>

                    <div className="event-program-row-meta">
                      <span className="compact-pill">{getLocation(eventProgram.location_id)}</span>
                      <span className="compact-pill">{getCoordinatorId(eventProgram.coordinator_id)}</span>
                      <span className="compact-pill">
                        {scheduleSummary.dateCount} Date{scheduleSummary.dateCount === 1 ? '' : 's'}
                      </span>
                    </div>

                    <div className="event-program-row-schedule">
                      <small>{rowDateSummary}</small>
                      {scheduleSummary.dateCount > 0 && scheduleSummary.nextDate !== 'No upcoming dates' ? (
                        <small>{`Most recent: ${scheduleSummary.latestDate}`}</small>
                      ) : null}
                      <small>
                        {/* need to use 15 for local dev and 13 for production/live when it comes using ep.event_type_id and */}
                        {eventProgram.event_type_id === 13
                          ? `${scheduleSummary.privateSchools} schools linked`
                          : scheduleSummary.capacityTrackedDates > 0
                            ? `${scheduleSummary.totalAvailableSeats} seats left`
                            : `${scheduleSummary.totalSubmittedRequests} submissions`}
                      </small>
                    </div>

                    <div className="event-program-row-flags">
                      <span className={`event-access-badge ${eventProgram.event_type_id === 4 ? 'public' : 'private'}`}>
                        {getEventAccessLabel(eventProgram.event_type_id)}
                      </span>
                      <span className="event-access-copy">
                        {eventProgram.event_type_id === 4
                          ? 'Attendance comes from public form submissions.'
                          : `${scheduleSummary.privateStudents} students, ${scheduleSummary.privateTeachers} teachers tracked across private dates.`}
                      </span>
                    </div>

                    <div className="text-muted small event-program-expand">
                      {isOpen ? '▼ Collapse' : '▶ Expand'}
                    </div>
                  </div>
                </td>
              </tr>

              {isOpen && (
                <tr>
                  <td colSpan={1}>
                    <div className="event-program-detail-grid p-3 border rounded">
                      <div className="detail-panel">
                        <h6 className="mb-2">Overview</h6>
                        <div className="schedule-summary-row">
                          <strong>Location</strong>
                          <span>{getLocation(eventProgram.location_id)}</span>
                        </div>
                        <div className="schedule-summary-row">
                          <strong>Coordinator</strong>
                          <span>{getCoordinatorId(eventProgram.coordinator_id)}</span>
                        </div>
                        <div className="schedule-summary-row">
                          <strong>Type</strong>
                          <span>{getEventType(eventProgram.event_type_id)}</span>
                        </div>
                        <div className="mt-2 text-muted small">
                          {eventProgram.description || 'No description provided.'}
                        </div>
                      </div>

                      <div className="detail-panel">
                        <h6 className="mb-2">Attendance Mode</h6>
                        <div className="mb-2">
                          <span className={`event-access-badge ${eventProgram.event_type_id === 4 ? 'public' : 'private'}`}>
                            {eventProgram.event_type_id === 4 ? 'Available for Form Submissions' : 'Private Attendance Counts'}
                          </span>
                        </div>
                        <div className="text-muted small">
                          {eventProgram.event_type_id === 4
                            ? 'This event appears on the public form submission page. Student and teacher totals come from individual form submissions tied to the selected date.'
                            : 'This event stays off the public form. Admins enter school-level student and teacher totals for each event date.'}
                        </div>
                      </div>

                      <div className="detail-panel">
                        <h6 className="mb-2">Schedule Summary</h6>
                        <div className="schedule-summary-row">
                          <strong>Dates</strong>
                          <span>{scheduleSummary.dateCount}</span>
                        </div>
                        <div className="schedule-summary-row">
                          <strong>Next Date</strong>
                          <span>{scheduleSummary.nextDate}</span>
                        </div>
                        <div className="schedule-summary-row">
                          <strong>Most Recent</strong>
                          <span>{scheduleSummary.latestDate}</span>
                        </div>
                        {/* need to use 15 for local dev and 13 for production/live when it comes using ep.event_type_id and */}
                        {eventProgram.event_type_id === 13 ? (
                          <>
                            <div className="schedule-summary-row">
                              <strong>Schools Linked</strong>
                              <span>{scheduleSummary.privateSchools}</span>
                            </div>
                            <div className="schedule-summary-row">
                              <strong>Tracked Attendance</strong>
                              <span>{`${scheduleSummary.privateStudents} students, ${scheduleSummary.privateTeachers} teachers`}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="schedule-summary-row">
                              <strong>Form Requests</strong>
                              <span>{scheduleSummary.totalSubmittedRequests}</span>
                            </div>
                            <div className="schedule-summary-row">
                              <strong>Seats Left</strong>
                              <span>{scheduleSummary.capacityTrackedDates > 0 ? scheduleSummary.totalAvailableSeats : 'Seat caps not set'}</span>
                            </div>
                            <div className="schedule-summary-row">
                              <strong>Sold Out Dates</strong>
                              <span>{scheduleSummary.soldOutDates}</span>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="detail-panel actions-panel">
                        <h6 className="mb-2">Actions</h6>
                        <Button type="button" onClick={() => editEventProgram(eventProgram)}>
                          <i className="bi bi-pencil"></i> Edit Event
                        </Button>
                        <Button type="button" onClick={() => handleNewEventDate(eventProgram)}>
                          New Date
                        </Button>
                        <Button type="button" variant="danger" onClick={() => deleteEventProgram(eventProgram.event_id)}>
                          <i className="bi bi-trash3"></i> Delete Event
                        </Button>
                      </div>

                      <div className="detail-panel event-program-dates-detail">
                        <div className="schedule-panel-toolbar">
                          <div>
                            <div className="schedule-panel-title">Dates for {eventProgram.name}</div>
                            <div className="schedule-panel-subtitle">
                              {/* need to use 15 for local dev and 13 for production/live when it comes using ep.event_type_id and */}
                              {eventProgram.event_type_id === 13
                                ? 'Private attendance dates show linked schools and teacher/student counts.'
                                : 'Public attendance dates collect counts from form submissions.'}
                            </div>
                          </div>
                          <Button type="button" onClick={() => handleNewEventDate(eventProgram)}>
                            New Date
                          </Button>
                        </div>
                        {renderEventDates(eventProgram)}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
        </>
      )}
        <Modal show={showEventDateModal} onHide={handleCancel}>
          <Modal.Header>{currentEventDate.event_dates_id ? 'Edit Event Date' : 'Create New Event Date'}</Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Select Date</Form.Label>
                <Form.Control
                type={'date'}
                id='date'
                name='date'
                value={currentEventDate.date || ''}
                onChange={handleEventDateFormChange}
                required>
                </Form.Control>
              </Form.Group>
              <Form.Group>
                <Form.Label>Start Time</Form.Label>
                <Form.Control
                type={'time'}
                id='start_time'
                name='start_time'
                value={currentEventDate.start_time || ''}
                onChange={handleEventDateFormChange}
                required>
                </Form.Control>
              </Form.Group>
              <Form.Group>
                <Form.Label>End Time</Form.Label>
                <Form.Control
                id='end_time'
                value={currentEventDate.end_time || ''}
                type={'time'}
                name='end_time'
                onChange={handleEventDateFormChange}>
                </Form.Control>
              </Form.Group>
              <Form.Group>
              <Form.Label>Total Number of Seats</Form.Label>
              <Form.Control
                type="number"
                id="total_seats"
                name="total_seats"
                value={currentEventDate.total_seats ?? ''}
                onChange={handleEventDateFormChange}>
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Check 
                type="checkbox"
                id="manual_sold_out"
                name="manual_sold_out"
                checked={Boolean(currentEventDate.manual_sold_out)}
                onChange={handleEventDateFormChange}
                label="Force Sold Out" />
              <Form.Text muted>
                Dates also close automatically once form submissions reach the seat cap.
              </Form.Text>
            </Form.Group>
            {!isDateProgramPrivate ? (
              <div className="mt-3 text-muted small">
                {normalizeOptionalInt(currentEventDate.total_seats) === null
                  ? 'Set a seat cap to track remaining availability for this public event date.'
                  : `${getEventDateAvailableSeats(currentEventDate)} seat${getEventDateAvailableSeats(currentEventDate) === 1 ? '' : 's'} left from ${normalizeOptionalInt(currentEventDate.total_seats)} total. ${getEventDateSubmissionCount(currentEventDate)} form submission${getEventDateSubmissionCount(currentEventDate) === 1 ? '' : 's'} currently tied to this date.`}
              </div>
            ) : null}
            {isDateProgramPrivate ? (
              <>
                <hr />
                <p className="mb-2 fw-bold">Private Attendance by School</p>
                {dateSchools.map((dateSchool, index) => (
                  <div key={`${dateSchool.school_id || 'new'}-${index}`} className="border rounded p-3 mb-3">
                    <Form.Group className="mb-2">
                      <Form.Label>School</Form.Label>
                      <Form.Control
                        as="select"
                        value={dateSchool.school_id}
                        onChange={(event) => handleDateSchoolFormChange(index, 'school_id', event.target.value)}
                      >
                        <option value="">Select School</option>
                        {schools.map((school) => (
                          <option key={school.school_id} value={school.school_id}>
                            {school.name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Number of Students</Form.Label>
                      <Form.Control
                        type="number"
                        value={dateSchool.number_of_students}
                        onChange={(event) => handleDateSchoolFormChange(index, 'number_of_students', event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Number of Teachers</Form.Label>
                      <Form.Control
                        type="number"
                        value={dateSchool.number_of_teachers}
                        onChange={(event) => handleDateSchoolFormChange(index, 'number_of_teachers', event.target.value)}
                      />
                    </Form.Group>
                    <Button
                      type="button"
                      variant="outline-danger"
                      onClick={() => removeDateSchool(index)}
                      disabled={dateSchools.length === 1}
                    >
                      Remove School
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline-primary" onClick={addDateSchool}>
                  Add School
                </Button>
              </>
            ) : (
              <p className="mt-3 mb-0 text-muted">
                Attendance counts for this event are collected from public form submissions.
              </p>
            )}

            </Form>
            <Modal.Footer className='modal-footer-buttons'>
              {currentEventDate.event_dates_id ? 
              <Button type="button" variant='danger' onClick={() => {handleDeleteEventDate()}}>Delete</Button>
               : 
               ''}
              <Button type="button" onClick={handleEventDateSubmit}>{currentEventDate.event_dates_id ? 'Update' : 'Create'}</Button>
              <Button type="button" variant='secondary' onClick={handleCancel}>Cancel</Button>
           </Modal.Footer>
          </Modal.Body>
        </Modal>
        <Modal show={showDeletionError} onHide={() => {setShowDeletionError(false)}}>
          <Modal.Header>Unable to process Request</Modal.Header>
          <Modal.Body>Please be sure to delete all associated dates with the event before deleting the event</Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setShowDeletionError(false)}>Close</Button>
          </Modal.Footer>
        </Modal>
        <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)}>
          <Modal.Header closeButton>{'Please Confirm!'}</Modal.Header>
          <Modal.Body>{confirmationMessage}</Modal.Body>
          <Modal.Footer>
            <Button variant='danger' onClick={() => deleteEventDate(currentEventDate.event_dates_id)}>{'Delete'}</Button>
            <Button variant='secondary' onClick={() => setShowConfirmationModal(false)}>{'Cancel'}</Button>
          </Modal.Footer>
        </Modal>
        <ErrorModalMessage errorMessage={errorMessage} clearErrorMessage={() => setErrorMessage('')}/>

        
    </div>
  );
};

export default EventsProgramComponent;
